import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { accessOf, actionState, NO_ROLE_REASON, STAFF_VIEW_REASON, type Audience, type Identity, type Permission } from '@infinix/registry';
import type { Product } from '@infinix/tokens';
import { accountApi, type BusinessUnit, type SessionView } from './account';
import { lastServerContact, onSessionEnded, setCsrf } from './api';
import { lastUserActivity, markUserActivity, trackUserActivity } from './activity';

/**
 * The one Nest session (decision A-25), as every app sees it: who is signed in, their organization, business units,
 * selected business unit and Core roles, read from the account backend (`/account/api/session`). The backend holds the
 * real session in an HttpOnly cookie and decides the scope; this mirrors it and reacts to it changing.
 *
 * Cached data belongs to one session and one scope: it is cleared when a session starts or ends and when the business
 * unit changes, here or in another tab (the cookie is shared by every tab and every app). The idle timeout counts real
 * input in any tab (activity.ts); a light check-in keeps the backend's own idle clock in step.
 */

export const SESSION_KEY = ['nest-session'] as const;
/** Person + organization: a CSRF retry only runs for the same owner (api.ts). */
const ownerOf = (v: SessionView) => `${v.user.email.toLowerCase()}|${v.organization?.id ?? 'platform'}`;
const CHANNEL = 'nest-session';
const WARN_BEFORE_IDLE_SEC = 120;
const HEARTBEAT_EVERY_MS = 5 * 60_000;
const ACTIVE_WITHIN_MS = 60_000;

type Message = { type: 'signed-out' } | { type: 'signed-in' } | { type: 'scope-changed' };
export type EndReason = 'idle' | 'expired' | 'signed-out';

/** The suite product of a Core product code (shared contract; Core's "nest" is the Stay app, A-24). */
export { productOf } from '@infinix/contract';
import { productOf } from '@infinix/contract';

export interface Session {
  status: 'loading' | 'signed-in' | 'signed-out' | 'error';
  /** The session could not be read (backend unreachable), as opposed to signed out. */
  error: unknown;
  /** Why the last session ended, for the sign-in page. */
  endReason: EndReason | null;
  view: SessionView | null;
  email: string | null;
  org: { id: string; name: string } | null;
  /** 'platform' = Nest staff (no organization; their tools are in Ops). */
  level: 'org' | 'bu' | 'platform' | null;
  units: readonly BusinessUnit[];
  /** Selected business unit; null = "All business units" (organization-level people only). */
  buId: string | null;
  roles: readonly string[];
  identity: Identity | null;
  audience: Audience;
  /** Organization + business unit: the first part of every app's data query keys. */
  scopeKey: string;
  /** May see it: pages, menus, data. Staff looking at a tenant see what its admins see (A-34). */
  can: (perm: Permission | undefined) => boolean;
  /** May do it: actions that change something. Only one's own permissions: never the viewed tenant admin's. */
  canAct: (perm: Permission | undefined) => boolean;
  /**
   * Nest staff looking at a tenant (one picked in the top bar): they see the tenant's apps as its admins do, read-only
   * (A-34). False for customers, and for staff at All tenants.
   */
  staffView: boolean;
  /** Start using a session the sign-in page just created. */
  signedIn: (view: SessionView) => void;
  /** Ends the session at the backend; rejects when it did not (the person stays signed in). */
  signOut: (reason?: EndReason) => Promise<void>;
  setBuId: (id: string | null) => Promise<void>;
  /** Staff: look at one tenant, or at all tenants (null). */
  setTenant: (id: string | null) => Promise<void>;
  idleWarning: { secondsLeft: number } | null;
  staySignedIn: () => Promise<void>;
  retry: () => void;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: SESSION_KEY, queryFn: accountApi.session, retry: false, staleTime: 60_000 });
  const [endReason, setEndReason] = useState<EndReason | null>(null);
  const [idleWarning, setIdleWarning] = useState<{ secondsLeft: number } | null>(null);
  const channel = useRef<BroadcastChannel | null>(null);
  const view = query.data ?? null;

  useEffect(() => {
    setCsrf(view?.csrfToken ?? null, view ? ownerOf(view) : null, async () => {
      const v = await accountApi.session();
      return v ? { csrf: v.csrfToken, owner: ownerOf(v) } : null;
    });
  }, [view]);

  /** Drops every cached query except the session itself. */
  const clearData = useCallback(() => {
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] });
  }, [queryClient]);

  const ended = useCallback(
    (reason: EndReason) => {
      // The first reason wins: a request still in flight at the idle sign-out can return 401 afterwards.
      if (queryClient.getQueryData(SESSION_KEY) === null) return;
      setEndReason(reason);
      setIdleWarning(null);
      setCsrf(null);
      queryClient.setQueryData(SESSION_KEY, null);
      clearData();
    },
    [queryClient, clearData],
  );

  const post = (m: Message) => channel.current?.postMessage(m);

  // Other tabs: sign-in, sign-out and business-unit changes (no data in the messages).
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(CHANNEL);
    channel.current = ch;
    ch.onmessage = (e: MessageEvent<Message>) => {
      if (e.data?.type === 'signed-out') ended('signed-out');
      if (e.data?.type === 'signed-in' || e.data?.type === 'scope-changed') {
        // Nothing renders until the new session is read: resetting it (synchronously) puts every gate back to loading,
        // which unmounts the screens and their queries; then the old data is dropped.
        void queryClient.resetQueries({ queryKey: SESSION_KEY });
        clearData();
      }
    };
    return () => {
      ch.close();
      channel.current = null;
    };
  }, [ended, clearData, queryClient]);

  // Any backend call that finds the session gone ends it here.
  useEffect(() => onSessionEnded(() => ended('expired')), [ended]);

  // Idle timeout from real input in any tab; a light check-in keeps the backend's idle backstop in step.
  useEffect(() => {
    if (!view) return;
    markUserActivity();
    const stopTracking = trackUserActivity();
    let checkingIn = false;
    const tick = () => {
      const now = Date.now();
      if (now - lastUserActivity() < ACTIVE_WITHIN_MS && now - lastServerContact() >= HEARTBEAT_EVERY_MS && !checkingIn) {
        checkingIn = true;
        void accountApi
          .session()
          .then((v) => (v ? queryClient.setQueryData(SESSION_KEY, v) : ended('expired')))
          .catch(() => undefined)
          .finally(() => {
            checkingIn = false;
          });
      }
      const left = Math.ceil(view.idleTimeoutSec - (now - lastUserActivity()) / 1000);
      if (left <= 0) {
        void accountApi.signOut().catch(() => undefined);
        post({ type: 'signed-out' });
        ended('idle');
      } else if (left <= WARN_BEFORE_IDLE_SEC) {
        setIdleWarning({ secondsLeft: left });
      } else {
        setIdleWarning(null);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => {
      window.clearInterval(id);
      stopTracking();
    };
  }, [view, ended, queryClient]);

  const value = useMemo<Session>(() => {
    const identity: Identity | null = view
      ? { roles: view.roles, products: [...new Set(view.businessUnits.map((b) => productOf(b.productCode)).filter((p): p is Product => p !== null))] }
      : null;
    // Who may see and do what (A-22, A-34): @infinix/registry access.ts, tested there.
    const access = accessOf({ roles: view?.roles ?? [], orgPicked: Boolean(view?.organization), unitPicked: Boolean(view?.activeBusinessUnitId) });
    return {
      status: query.isPending ? 'loading' : view ? 'signed-in' : query.isError ? 'error' : 'signed-out',
      error: query.error,
      endReason,
      view,
      email: view?.user.email ?? null,
      org: view?.organization ?? null,
      level: view?.level ?? null,
      units: view?.businessUnits ?? [],
      buId: view?.activeBusinessUnitId ?? null,
      roles: view?.roles ?? [],
      identity,
      audience: identity ? access.audience : 'customer',
      scopeKey: view ? `${view.organization?.id ?? 'platform'}:${view.activeBusinessUnitId ?? 'all'}` : 'none',
      can: access.can,
      staffView: access.staffView,
      canAct: access.canAct,
      signedIn: (v) => {
        setEndReason(null);
        clearData();
        queryClient.setQueryData(SESSION_KEY, v);
        post({ type: 'signed-in' });
      },
      signOut: async (reason = 'signed-out') => {
        await accountApi.signOut();
        post({ type: 'signed-out' });
        ended(reason);
      },
      setBuId: async (id) => {
        const v = await accountApi.selectBusinessUnit(id);
        clearData();
        queryClient.setQueryData(SESSION_KEY, v);
        post({ type: 'scope-changed' });
      },
      setTenant: async (id) => {
        const v = await accountApi.selectTenant(id);
        clearData();
        queryClient.setQueryData(SESSION_KEY, v);
        post({ type: 'scope-changed' });
      },
      idleWarning,
      staySignedIn: async () => {
        markUserActivity();
        const v = await accountApi.session();
        if (v) queryClient.setQueryData(SESSION_KEY, v);
        else ended('expired');
      },
      retry: () => void query.refetch(),
    };
  }, [view, query, endReason, idleWarning, queryClient, ended, clearData]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used inside <SessionProvider>');
  return v;
}

/** The selected business unit, or null for "All business units". */
export function useScope() {
  const s = useSession();
  const bu = s.units.find((u) => u.id === s.buId) ?? null;
  return { org: s.org, bu, units: s.units, label: bu ? bu.name : 'All business units' };
}

export { NO_ROLE_REASON, STAFF_VIEW_REASON };

/**
 * Whether the signed-in person may perform an action, and why not. Pass the result's `reason` to `<Btn
 * disabledReason>`. `extra` adds a screen-specific rule, explained by `deniedReason`. The backend enforces the same.
 */
export function usePermission(perm: Permission, extra = true, deniedReason?: string): { ok: boolean; reason: string | undefined } {
  const s = useSession();
  return actionState(s, perm, extra, deniedReason);
}
